import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { api } from "~/api";
import { Button } from "~/components/Button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/card";
import { Icon } from "~/components/icon";
import { OrgListItem } from "~/components/OrgListItem";
import { SessionExpired } from "~/components/SessionExpired";
import { SlackChannelLink } from "~/components/SlackChannelLink";
import { useAuth } from "~/contexts/auth";
import { useKeybindings } from "~/hooks/useKeyBindings";
import { ClusterOperationsCommandPalette } from "~/routes/_index/components/ClusterOperationsCommandPalette";
import type { CustomerDetail } from "~/types";
import { getWorkOSUrl } from "~/utils";
import { formatRelativeUtc } from "~/utils/time";

function CustomerOrgs({ orgs }: { orgs: CustomerDetail["orgs"] }) {
  if (!orgs?.length) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-medium text-gray-700">Orgs</h3>
        <p className="text-sm text-gray-500">
          No orgs are attached to this customer.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-2 text-sm font-medium text-gray-700">Orgs</h3>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {orgs.map((org) => (
          <OrgListItem
            key={org.id}
            id={org.id}
            name={org.name}
            isByoc={org.is_byoc}
            href={`/orgs/${org.id}`}
            stats={{
              namespaceCount: org.namespace_count,
              totalVectors: org.total_vectors,
              totalBytes: org.total_bytes,
            }}
          />
        ))}
      </div>
    </div>
  );
}

function CustomerDetailsCard({ customer }: { customer: CustomerDetail }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {customer.name}
          {customer.deactivated_at ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700"
              title={`Deactivated ${customer.deactivated_at}`}
            >
              Deactivated
            </span>
          ) : null}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Customer ID
              </div>
              <div className="mt-1 font-mono text-sm text-gray-900">
                {customer.id}
              </div>
            </div>

            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                WORKOS ID
              </div>
              <div className="mt-1 font-mono text-sm text-gray-900">
                {customer.workos_org_id ?? "—"}
              </div>
            </div>

            {customer.slack_channel ? (
              <div className="col-span-2 rounded-lg bg-gray-50 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Slack Channel
                </div>
                <SlackChannelLink channel={customer.slack_channel} />
              </div>
            ) : null}
          </div>

          {customer.deactivated_at ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
              This customer was deactivated{" "}
              {formatRelativeUtc(new Date(customer.deactivated_at), new Date())}
              .
            </div>
          ) : null}

          <CustomerOrgs orgs={customer.orgs} />

          {customer.workos_org_id ? (
            <div>
              <h3 className="mb-2 text-sm font-medium text-gray-700">Links</h3>
              <div className="flex flex-wrap gap-2">
                <a
                  href={getWorkOSUrl(customer.workos_org_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-900 transition-colors hover:bg-gray-200"
                >
                  <Icon
                    name="ExternalLink"
                    size={16}
                    className="text-blue-600"
                  />
                  WorkOS
                </a>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CustomerDetails() {
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const navigate = useNavigate();
  const { customerId } = useParams();
  const { status, setIsAuthorized } = useAuth();

  // Cmd+K to open command palette
  useKeybindings(
    useMemo(
      () => ({
        "$mod+k": (e: KeyboardEvent) => {
          e.preventDefault();
          setIsCommandPaletteOpen(true);
        },
      }),
      [],
    ),
    {
      enabled: !isCommandPaletteOpen,
    },
  );

  const fetchCustomer = useCallback(
    async (customerId: string) => {
      setError(null);
      setCustomer(null);
      try {
        const data = await api.getCustomer(customerId);
        setCustomer(data);
        setError(null);
      } catch (err: any) {
        if (err.status === 401) setIsAuthorized(false);
        setError(err.message ?? "Failed to fetch customer");
      }
    },
    [setIsAuthorized],
  );

  useEffect(() => {
    if (customerId) {
      fetchCustomer(customerId);
    }
  }, [customerId, fetchCustomer]);

  if (status !== "authorized") {
    return <SessionExpired />;
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Error</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600">{error}</p>
          <Button className="mt-4" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!customer) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Loading...</CardTitle>
        </CardHeader>
        <CardContent>
          <p>Loading customer details...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <CustomerDetailsCard customer={customer} />

      <ClusterOperationsCommandPalette
        isOpen={isCommandPaletteOpen}
        setIsOpen={setIsCommandPaletteOpen}
        clusters={[]}
      />
    </>
  );
}
